import React, { useRef, useState } from 'react';
import { useField } from 'formik';
import { IMaskInput } from 'react-imask';
import IMask from 'imask/esm/imask';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';
import moment from 'moment';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';

import * as Icon from 'components/Icon';
import useOnClickOutside from 'helpers/useClickOutside';
import { FIELD_DATE_DIRECTION } from 'modules/process/constants';

import Field from '../Field';

import classes from '../Field.module.scss';
import 'react-calendar/dist/Calendar.css';

interface IProps {
  name: string;
  label: string;
  defaultValue?: string;
  placeholder?: string;
  showError?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  readOnly?: boolean;

  min?: number | null;
  max?: number | null;
  decimal?: boolean;
  direction?: FIELD_DATE_DIRECTION | null | '';

  [key: string]: any;
}

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isDateAllowed = (date: Date, direction?: FIELD_DATE_DIRECTION | null | '') => {
  if (!direction || Number.isNaN(date.getTime())) return true;
  const today = getToday();
  if (direction === FIELD_DATE_DIRECTION.BEFORE) return date < today;
  if (direction === FIELD_DATE_DIRECTION.AFTER) return date >= today;
  return true;
};

const DateField: React.FC<IProps> = ({ label, placeholder, showError = true, min, max, disabled, readOnly, decimal, autoFocus, required, direction, ...props }) => {
  const { t } = useTranslation();
  const [calendar, setCalendar] = useState<boolean>(false);
  const ref = useRef(null);
  useOnClickOutside(ref, () => setCalendar(false));

  const [field, meta, helpers] = useField(props);
  const hasError = !!(showError && meta.touched && meta.error);

  const handleDateChange = (e) => {
    if (!isDateAllowed(e, direction)) return;
    helpers.setValue(moment(e).format('DD.MM.YYYY'));
    setCalendar(false);
  };

  const calendarLimits =
    direction === FIELD_DATE_DIRECTION.BEFORE
      ? { maxDate: new Date(getToday().getTime() - 86400000) }
      : direction === FIELD_DATE_DIRECTION.AFTER
        ? { minDate: new Date(getToday().getTime()) }
        : {};

  return (
    <Field {...{ name: field.name, label, hasError, showError, error: meta.error, autoFocus, disabled, required, readOnly }} icon={
      <div className={classes.calendar}>
        <div onClick={() => setCalendar(prev => !prev)}>
          <Icon.System.Calendar width="20" height="20" />
        </div>
        <Transition in={calendar} timeout={{ enter: 0, exit: 200 }} unmountOnExit mountOnEnter>
          {(state: TransitionStatus) => (
            <div style={{ ...transition[state] }} ref={ref} className={classes.calendarContainer}>
              <Calendar onChange={handleDateChange} {...calendarLimits} className={classes.calendarBase} />
            </div>
          )}
        </Transition>
      </div>
    }>
      {({ onFocus, onBlur }) => (
        <IMaskInput
          {...{ placeholder, disabled, onFocus, readOnly }}
          id={field.name}
          name={field.name}
          value={field.value}
          type="tel"
          onBlur={(e) => {
            field.onBlur(e);
            onBlur();
          }}
          onAccept={value => helpers.setValue(value)}
          className={classes.input}
          placeholderChar=" "
          overwrite
          unmask
          lazy={false}
          autofix
          mask={Date}
          blocks={{
            d: { mask: IMask.MaskedRange, placeholderChar: t('day_placeholder'), from: 1, to: 31, maxLength: 2 },
            m: { mask: IMask.MaskedRange, placeholderChar: t('month_placeholder'), from: 1, to: 12, maxLength: 2 },
            Y: { mask: IMask.MaskedRange, placeholderChar: t('year_placeholder'), from: 1500, to: 2999, maxLength: 4 }
          }}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
      )}
    </Field>
  );
};

const transition = {
  entering: { opacity: 0, visibility: 'hidden' },
  entered: { opacity: 1, visibility: 'visible', transition: 'all .2s ease-in-out' },
  exited: { opacity: 0, visibility: 'hidden' }
};

export default DateField;
